import { Extras } from "@sentry/types";
import { TemplateConfig } from "@types";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ROUTE_SLUGS } from "src/config/navigation/routes";
import { ROUTES } from "../config";
import { Device, Document, RoadmapTasks, User } from "../stores/models";
import {
  canPrecreateDocumentVersion,
  captureSentryException,
  fillRouteWithSlugs,
  getDocVersionStatus,
  getLatestDocumentVersion,
  isDocumentCompleted,
} from "../utils";
import {
  useCreateDocumentMutation,
  useCreateDocumentVersionMutation,
  usePatchDocumentVersionMutation,
} from "./mutations";
import { getDocumentsAsync } from "./queries";

const throwNavigationError = (msg: string, extras?: Extras) => {
  captureSentryException(msg, extras);
  throw new Error(msg);
};

/**
 * Get refreshed document and documents
 */
const refreshDocumentAndDocuments = async (
  orgId: string,
  deviceId: string,
  templateKey: string,
  templatesConfig: TemplateConfig,
  documentId?: string
) => {
  // Documents must be got with async to avoid stale closure
  let documents = await getDocumentsAsync(orgId, deviceId, templatesConfig);
  let document =
    documentId === undefined
      ? documents.find((doc) => doc.templateKey === templateKey)
      : documents.find((doc) => doc.id === documentId);
  return { documents, document };
};

// Shared state to track if any document operation is in progress
let globalPendingState = false;

// Use a Set to store listeners
const globalListeners = new Set<(pending: boolean) => void>();

/**
 * Updates the global pending state and notifies all listeners
 * This ensures all components using useDocEngine stay in sync
 */
const setGlobalPending = (pending: boolean) => {
  // Update the global state
  globalPendingState = pending;
  // Notify all listeners
  globalListeners.forEach((listener) => {
    listener(pending);
  });
};

export const useDocEngine = (deviceId: string) => {
  const navigate = useNavigate();
  const [fullPath, setFullPath] = useState("");
  const { pathname: currentPath, search } = useLocation();
  const { orgId = "" } = useParams();

  const [pending, setPending] = useState(globalPendingState);

  useEffect(() => {
    // Add the listener to the set
    globalListeners.add(setPending);

    setFullPath(currentPath + search);

    return () => {
      // Remove the listener when the component unmounts
      globalListeners.delete(setPending);
    };
  }, []);

  useEffect(() => {
    // Reset pending state when pathname/search changes (navigation completes)
    if (fullPath !== currentPath + search) {
      setGlobalPending(false);
    }
  }, [currentPath, search]);

  const createDocumentMutation = useCreateDocumentMutation();
  const createDocumentVersionMutation = useCreateDocumentVersionMutation(
    orgId,
    deviceId
  );
  const patchDocumentMutation = usePatchDocumentVersionMutation();

  /**
   * Get an existing doc, or dynamically create one (usually on first navigation)
   * This should likely only be used within navigateToWizardOrQMS.
   */
  const _getOrCreateDocument = async (
    existingDocumentId: string | undefined,
    templateKey: string,
    createNewMultiInstanceDoc: boolean,
    templatesConfig: TemplateConfig
  ) => {
    let { document } = await refreshDocumentAndDocuments(
      orgId,
      deviceId,
      templateKey,
      templatesConfig,
      existingDocumentId
    );

    // If a `document` id is supplied but it doesn't exist, throw an error
    if (existingDocumentId && !document) {
      throwNavigationError(
        "Cannot navigate to a documentId that doesn't exist",
        {
          existingDocumentId,
        }
      );
    }

    // Create a new document if none exists
    if (!document || createNewMultiInstanceDoc) {
      document = await createDocumentMutation.mutateAsync({
        orgId,
        templateKey,
        deviceId,
      });
    }

    return document;
  };

  /**
   * Navigate to the correct view for a document,
   * and potentially create a document version.
   * This should likely only be used within navigateToWizardOrQMS.
   */
  const _navigateToDoc = async (
    templateKey: string,
    createNewDocVersion: boolean,
    document: Document,
    device: Device,
    // @ts-ignore
    documents: Document[],
    user: User,
    templatesConfig: TemplateConfig,
    tasks: RoadmapTasks
  ) => {
    let qmsUrl = fillRouteWithSlugs(ROUTES.QMS_OPEN_DOC, {
      [ROUTE_SLUGS.ORG_ID]: orgId,
      [ROUTE_SLUGS.DEVICE_ID]: deviceId,
      [ROUTE_SLUGS.TEMPLATE_KEY]: templateKey,
    });

    const templateConfig = templatesConfig[templateKey];

    // Are we allowed to precreate a new document version? (i.e file uploads shouldn't)
    if (!canPrecreateDocumentVersion(templateConfig)) {
      return navigate(qmsUrl);
    }

    // Create new documentVersion if there isn't one or if explicitly requested
    if (createNewDocVersion || document.versions.length === 0) {
      await createDocumentVersionMutation.mutateAsync({
        docId: document.id,
      });
    }

    // Refresh documents/document
    const { document: documentUpdated, documents: documentsUpdated } =
      await refreshDocumentAndDocuments(
        orgId,
        deviceId,
        templateKey,
        templatesConfig,
        document.id
      );
    if (documentUpdated === undefined) {
      return throwNavigationError("Client Error: Document not returned.");
    }

    const docVersion = getLatestDocumentVersion(documentUpdated);

    if (!docVersion) {
      return throwNavigationError(
        "Client Error: Document version not returned."
      );
    }

    if (
      isDocumentCompleted({
        document: documentUpdated,
        device,
        documents: documentsUpdated,
        documentVersion: docVersion,
        templateConfig: templatesConfig[templateKey],
      })
    ) {
      return navigate(
        (qmsUrl += `?d=${documentUpdated.id}&v=${docVersion.id}`)
      );
    } else {
      const docVersionStatus = getDocVersionStatus({
        document: documentUpdated,
        documents: documentsUpdated,
        selectedDocVersion: docVersion,
        user,
        templateKey,
        orgId,
        device,
        templatesConfig,
        tasks,
      });
      // If for some reason the document is live but above isDocumentCompleted is false we need to create a new version to allow the user to complete it. This could happen if a new data key was added to the document after the document was approved.
      if (docVersionStatus === "LIVE") {
        await createDocumentVersionMutation.mutateAsync({
          docId: documentUpdated.id,
        });
        await refreshDocumentAndDocuments(
          orgId,
          deviceId,
          templateKey,
          templatesConfig,
          documentUpdated.id
        );
      } else if (docVersion.readyForApproval) {
        // If the document is not completed, navigate to the template assistant also for safety set readyForApproval to false
        await patchDocumentMutation.mutateAsync({
          orgId,
          docId: documentUpdated.id,
          deviceId,
          docVersionId: docVersion.id,
          data: { readyForApproval: false },
        });

        await refreshDocumentAndDocuments(
          orgId,
          deviceId,
          templateKey,
          templatesConfig,
          documentUpdated.id
        );
      }

      return navigate(
        fillRouteWithSlugs(ROUTES.TEMPLATE_ASSISTANT, {
          [ROUTE_SLUGS.ORG_ID]: orgId,
          [ROUTE_SLUGS.DEVICE_ID]: deviceId,
          [ROUTE_SLUGS.TEMPLATE_KEY]: templateKey,
          [ROUTE_SLUGS.DOC_ID]: documentUpdated.id,
        }),
        {
          state: {
            from: currentPath,
          },
        }
      );
    }
  };

  /**
   * Navigation logic for a document
   * Navigates to the QMS or Wizard based on the document state
   * Creates a new document if none exists
   * Creates a new docVersionif necessary
   * @param templateKey
   * @param createNewMultiInstanceDoc Explicitly create a new multi-instance document
   * @param createNewDocVersion Explicitly create a new Document Version
   * @param documentId Useful to navigate to a multi instance document
   * @returns void
   */
  const navigateToWizardOrQMS = async ({
    templateKey,
    createNewMultiInstanceDoc = false,
    createNewDocVersion = false,
    documentId,
    device,
    documents,
    user,
    templatesConfig,
    tasks,
  }: {
    templateKey: string;
    createNewMultiInstanceDoc?: boolean;
    createNewDocVersion?: boolean;
    documentId?: string;
    device: Device;
    documents: Document[];
    user: User;
    templatesConfig: TemplateConfig;
    tasks: RoadmapTasks;
  }) => {
    setGlobalPending(true);
    try {
      let document = await _getOrCreateDocument(
        documentId,
        templateKey,
        createNewMultiInstanceDoc,
        templatesConfig
      );
      await _navigateToDoc(
        templateKey,
        createNewDocVersion,
        document,
        device,
        documents,
        user,
        templatesConfig,
        tasks
      );
    } catch (error) {
      setGlobalPending(false);
      throw error;
    }
  };

  return {
    navigateToWizardOrQMS,
    pending,
    _getOrCreateDocument,
    _navigateToDoc,
  };
};
